<template>
    <sl-card :class="{ 'sl-theme-dark': isDarkMode }">
        <template v-if="!isFetching">
            <div slot="header" class="card-header">
                <span v-if="typeLabel" class="entity-type-eyebrow">{{
                    typeLabel
                }}</span>
                <h3>
                    <font-awesome-icon
                        v-if="icon && Array.isArray(icon)"
                        :icon="icon"
                    />
                    <i v-else-if="icon" :class="icon" />
                    {{ entity?.name }}
                </h3>
            </div>
            <img
                slot="image"
                id="thumbnail"
                v-if="entity?.thumbnail"
                :src="entity?.thumbnail"
                alt="thumbnail"
                :style="imageStyle"
            />
            <p v-if="entity?.short_description" class="body">
                {{ entity?.short_description }}
            </p>
            <small v-else class="body no-description">No description</small>
            <div
                slot="footer"
                v-if="
                    tags?.length ||
                    showPath ||
                    indicators.length ||
                    entityRefs?.length
                "
            >
                <!-- Tags -->
                <div class="entity-tags" v-if="tags?.length">
                    <Tag
                        v-for="tag in tags"
                        class="tag"
                        :severity="tag.severity"
                    >
                        {{ tag.label }}
                    </Tag>
                </div>
                <!-- Indicator icons (mirror BPMN layer dots) -->
                <div class="indicator-icons" v-if="indicators.length">
                    <font-awesome-icon
                        v-for="indicator in indicators"
                        :key="indicator.key"
                        v-tooltip.top="indicator.tooltip"
                        class="indicator-icon"
                        :icon="indicator.icon"
                        :style="{ color: indicator.color }"
                    />
                </div>
                <!-- Path -->
                <div class="path" v-if="showPath">
                    <span v-if="domainName" class="segment">
                        <i class="pi pi-cloud"></i>
                        <span>{{ domainName }}</span>
                    </span>
                    <span v-if="roleName" class="segment">
                        <i class="pi pi-chevron-right"></i>
                        <i class="pi pi-id-card"></i>
                        <span>{{ roleName }}</span>
                    </span>
                    <span v-if="businessEntityName" class="segment">
                        <i class="pi pi-chevron-right"></i>
                        <i class="pi pi-building"></i>
                        <span>{{ businessEntityName }}</span>
                    </span>
                    <span v-if="productName" class="segment">
                        <i class="pi pi-chevron-right"></i>
                        <i class="pi pi-box"></i>
                        <span>{{ productName }}</span>
                    </span>
                    <span v-if="processName" class="segment">
                        <i class="pi pi-chevron-right"></i>
                        <i class="pi pi-share-alt"></i>
                        <span>{{ processName }}</span>
                    </span>
                    <span v-if="taskName" class="segment">
                        <i class="pi pi-chevron-right"></i>
                        <i class="pi pi-check-square"></i>
                        <span>{{ taskName }}</span>
                    </span>
                </div>
            </div>
        </template>
        <template v-else>
            <sl-skeleton slot="header" effect="sheen"></sl-skeleton>
            <p class="body">
                <sl-skeleton effect="sheen"></sl-skeleton>
                <sl-skeleton effect="sheen"></sl-skeleton>
            </p>
        </template>
    </sl-card>
</template>

<script setup lang="ts">
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/skeleton/skeleton.js";
import "@shoelace-style/shoelace/dist/themes/dark.css";
import "@shoelace-style/shoelace/dist/themes/light.css";

import Tag from "primevue/tag";
import { computed } from "vue";

import { useLayerVisibility } from "@/composables/bpmn-manager/layer";
import { useUIManager } from "@/stores/ui-store";

import { entityTypeLabel } from "./entity-type-label";

const props = withDefaults(
    defineProps<{
        entity: {
            identity: string;
            name: string;
            short_description?: string | null;
            thumbnail?: string | null;
        };
        tags: {
            label: string;
            severity: "primary" | "secondary" | "info";
        }[];
        icon?: string | string[];
        domainName?: string;
        productName?: string;
        processName?: string;
        taskName?: string;
        roleName?: string;
        businessEntityName?: string;
        entityType: string;
        backgroundColor?: string;
        textColor?: string;
        borderColor?: string;
        borderWidth?: string | null;
        showPath?: boolean;
        isFetching?: boolean;
        entityRefs?: Array<{
            entity_id: string;
            label: string;
            entityName: string;
        }>;
        generatesEvidence?: boolean;
        evidenceInstructions?: string;
        documentCount?: number;
        applicationCount?: number;
        linkedEntityCount?: number;
    }>(),
    {
        showPath: true,
        borderWidth: "var(--stekz-border-width)",
        backgroundColor: "Canvas",
        textColor: "light-dark(var(--stekz-black), var(--stekz-white))",
    }
);

// Show the entity kind as an eyebrow above the name so a hover card is
// self-describing — a Task reads as a Task, not a guess from the icon.
const typeLabel = computed(() => entityTypeLabel(props.entityType));

const layerVisibility = useLayerVisibility();

const indicatorColor = (variable: string) =>
    typeof window === "undefined"
        ? ""
        : window
              .getComputedStyle(document.body)
              .getPropertyValue(variable)
              .trim();

interface Indicator {
    key: string;
    icon: [string, string];
    color: string;
    tooltip: string;
}

const indicators = computed<Indicator[]>(() => {
    const result: Indicator[] = [];

    if (layerVisibility.value.compliancy && props.generatesEvidence) {
        result.push({
            key: "compliancy",
            icon: ["fas", "shield"],
            color: indicatorColor("--stekz-compliancy-color"),
            tooltip:
                props.evidenceInstructions || "Generates compliance evidence",
        });
    }

    if (layerVisibility.value.documents && (props.documentCount ?? 0) > 0) {
        const count = props.documentCount ?? 0;
        result.push({
            key: "documents",
            icon: ["fas", "file"],
            color: indicatorColor("--stekz-documents-color"),
            tooltip:
                count === 1
                    ? "1 document attached"
                    : `${count} documents attached`,
        });
    }

    if (layerVisibility.value.linkedEntities) {
        if (props.entityRefs?.length) {
            const tooltip = props.entityRefs
                .map((ref) => `${ref.label} ${ref.entityName}`)
                .join("\n");
            result.push({
                key: "linkedEntities",
                icon: ["fas", "building"],
                color: indicatorColor("--stekz-linkedentities-color"),
                tooltip,
            });
        } else if ((props.linkedEntityCount ?? 0) > 0) {
            const count = props.linkedEntityCount!;
            result.push({
                key: "linkedEntities",
                icon: ["fas", "building"],
                color: indicatorColor("--stekz-linkedentities-color"),
                tooltip:
                    count === 1
                        ? "1 domain object linked"
                        : `${count} domain objects linked`,
            });
        }
    }

    if (
        layerVisibility.value.applications &&
        (props.applicationCount ?? 0) > 0
    ) {
        const count = props.applicationCount!;
        result.push({
            key: "applications",
            icon: ["fas", "display"],
            color: indicatorColor("--stekz-applications-color"),
            tooltip:
                count === 1
                    ? "1 application linked"
                    : `${count} applications linked`,
        });
    }

    return result;
});

const borderBottom = computed(() =>
    props.borderColor
        ? `${props.borderWidth} solid ${props.borderColor}`
        : "none"
);

const ui = useUIManager();
const isDarkMode = computed(() => ui.darkMode.value);

const imageStyle = computed(() => {
    // Invert colors for BPMN diagrams in dark mode
    if (isDarkMode.value && props.entityType.toLowerCase() === "process") {
        return { filter: "invert(1) hue-rotate(180deg)" };
    }
    return {};
});
</script>

<style scoped lang="scss">
sl-card {
    --border-width: 0;
    --border-radius: 0;
    --sl-panel-background-color: v-bind(props.backgroundColor);
    --heading-color: v-bind(props.textColor);
    --text-color: v-bind(props.textColor);
    --p-text-muted-color: v-bind(props.textColor);

    width: 32em;
    max-width: 400px;
    transition: all 250ms ease;

    border-bottom: v-bind(borderBottom);

    &::part(base) {
        transition: all 250ms ease;
    }
    &::part(body) {
        flex-grow: 1;
    }

    h3 {
        margin-bottom: 0.375em;
        margin-top: 0.375em;

        > i {
            margin-right: 0.5em;
        }
    }
}

/* Type eyebrow above the name (Task / Role / Domain object / …) so the card
   says what it is instead of leaving you to infer it from the icon. */
.card-header .entity-type-eyebrow {
    display: block;
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--text-color-secondary, #777);
}

.path {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.85rem;
    flex-wrap: wrap;

    .pi-chevron-right {
        font-size: 0.5rem;
    }
}

.segment {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    flex-wrap: nowrap;
    white-space: nowrap;
}

.entity-tags {
    margin-bottom: 1em;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.indicator-icons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}

.indicator-icon {
    font-size: 1rem;
    cursor: help;
}

.body {
    word-break: break-all;

    &.no-description {
        color: var(--p-text-muted-color);
        font-style: italic;
    }
}

sl-skeleton {
    width: 300px;
}

p sl-skeleton {
    &:not(:last-of-type) {
        margin-bottom: 1rem;
    }

    &:last-of-type {
        width: 250px;
    }
}

#thumbnail {
    max-height: 200px;
    object-fit: contain;
}
</style>
